import React, { useState, useEffect } from "react";
import { innovatorAPI } from "../../services/api";
import { useWallet } from "../../context/WalletContext";

// More comprehensive and abstract domains
const DOMAINS = [
  "AI & Machine Learning", 
  "DeFi & Finance", 
  "Climate & Sustainability", 
  "Gaming & Entertainment", 
  "Infrastructure & Tooling", 
  "Digital Arts & NFTs", 
  "Security & Privacy", 
  "Social Impact & Community", 
  "Web3 & Blockchain", 
  "Education & Knowledge",
  "Health & Wellness",
  "Supply Chain & Logistics",
  "Governance & DAOs",
  "Identity & Credentials",
  "Metaverse & Virtual Worlds",
  "Hardware & IoT",
  "Other"
];

// More comprehensive and abstract skills
const SKILLS = [
  "Smart Contract Development",
  "Frontend Development", 
  "Backend Development", 
  "Full-Stack Development",
  "UI/UX Design", 
  "Data Science & Analytics",
  "AI Engineering",
  "Product Management", 
  "Project Management",
  "Business Development",
  "Marketing & Growth",
  "Content Creation",
  "Community Management",
  "Tokenomics & Mechanism Design",
  "Cybersecurity",
  "DevOps & Infrastructure",
  "Research",
  "Legal & Compliance",
  "Technical Writing",
  "Visual Design & Graphics",
  "Fundraising",
  "Strategy & Operations"
];

function InnovatorForm({ 
  onSubmitSuccess = () => {}, 
  connectedWallet = "", 
  onClose = () => {},
  initialData = null,
  isUpdate = false
}) {
  const { isConnected } = useWallet();
  const [formData, setFormData] = useState({
    displayName: "",
    email: "",
    walletAddress: connectedWallet?.toLowerCase(),
    bio: "",
    roles: {
      creator: false,
      contributor: false,
      investor: false
    },
    creatorDomains: [],
    contributorSkills: [],
    socials: {
      email: "",
      github: "",
      linkedin: "",
      twitter: "",
      discord: "",
      telegram: "",
      website: ""
    },
    portfolioLinks: [""]
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (initialData) {
      setFormData({
        displayName: initialData.name || "",
        email: initialData.email || "",
        walletAddress: initialData.walletAddress?.toLowerCase() || connectedWallet?.toLowerCase(),
        bio: initialData.bio || "",
        roles: {
          creator: initialData.roles?.includes('creator') || false,
          contributor: initialData.roles?.includes('contributor') || false,
          investor: initialData.roles?.includes('investor') || false
        },
        creatorDomains: initialData.domains || [],
        contributorSkills: initialData.skills || [],
        socials: {
          email: initialData.socials?.email || "",
          github: initialData.socials?.github || "",
          linkedin: initialData.socials?.linkedin || "",
          twitter: initialData.socials?.twitter || "",
          discord: initialData.socials?.discord || "",
          telegram: initialData.socials?.telegram || "",
          website: initialData.socials?.website || ""
        },
        // Handle both portfolio and portfolioLinks for backward compatibility
        portfolioLinks: initialData.portfolio 
          ? initialData.portfolio.map(item => item.link) 
          : initialData.portfolioLinks || [""]
      });
    }
  }, [initialData, connectedWallet]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isConnected) {
      setError('Please connect your wallet to register');
      return;
    }

    // Validate required fields
    if (!formData.displayName || !formData.email) {
      setError('Please fill in all required fields');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address');
      return;
    }

    // Validate at least one role is selected
    // if (!Object.values(formData.roles).some(role => role)) {
    //   setError('Please select at least one role');
    //   return;
    // }

    setLoading(true);
    setError(null);

    try {
      // Create the final data object
      const dataToSubmit = {
        name: formData.displayName,
        email: formData.email,
        walletAddress: formData.walletAddress.toLowerCase(),
        bio: formData.bio || "",
        roles: Object.entries(formData.roles)
          .filter(([_, value]) => value)
          .map(([key]) => key),
        domains: formData.creatorDomains,
        skills: formData.contributorSkills,
        socials: {
          email: formData.socials.email?.trim() || "",
          github: formData.socials.github?.trim() || "",
          linkedin: formData.socials.linkedin?.trim() || "",
          twitter: formData.socials.twitter?.trim() || "",
          discord: formData.socials.discord?.trim() || "",
          telegram: formData.socials.telegram?.trim() || "",
          website: formData.socials.website?.trim() || ""
        },
        // Convert portfolioLinks array to the format expected by the backend
        portfolio: formData.portfolioLinks
          .filter(link => link.trim() !== '')
          .map(link => ({
            title: 'Portfolio Link',
            description: 'Portfolio work',
            link: link
          }))
      };

      let response;
      // if (isUpdate) {
      //   response = await innovatorAPI.update(formData.walletAddress, dataToSubmit);
      // } else {
      //   response = await innovatorAPI.create(dataToSubmit);
      // }
      response = await innovatorAPI.update(formData.walletAddress, dataToSubmit);
      
      if (typeof onSubmitSuccess === 'function') {
        onSubmitSuccess(response.data);
      }
      
      onClose();
    } catch (error) {
      setError(error.response?.data?.message || error.message || 'Failed to save profile');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleRoleChange = (role) => {
    setFormData(prev => ({
      ...prev,
      roles: {
        ...prev.roles,
        [role]: !prev.roles[role]
      }
    }));
  };

  const handleSocialChange = (platform, value) => {
    setFormData(prev => ({
      ...prev,
      socials: {
        ...prev.socials,
        [platform]: value
      }
    }));
  };

  const handlePortfolioLinkChange = (index, value) => {
    const newLinks = [...formData.portfolioLinks];
    newLinks[index] = value;
    setFormData(prev => ({
      ...prev,
      portfolioLinks: newLinks
    }));
  };

  const addPortfolioLink = () => {
    if (formData.portfolioLinks.length < 3) {
      setFormData(prev => ({
        ...prev,
        portfolioLinks: [...prev.portfolioLinks, ""]
      }));
    }
  };


  return (
    <form onSubmit={handleSubmit} className="space-y-8">
  {/* Error Alert */}
  {error && (
    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-md" role="alert">
      <span>{error}</span>
    </div>
  )}

  {/* Display Name + Email */}
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">Display Name *</label>
      <input
        type="text"
        name="displayName"
        value={formData.displayName}
        onChange={handleChange}
        required
        className="block w-full h-11 px-4 rounded-md border border-gray-300 focus:ring-indigo-500 focus:border-indigo-500 bg-white"
      />
    </div>

  {/* Wallet Address */}
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">Wallet Address</label>
    <input
      type="text"
      name="walletAddress"
      value={formData.walletAddress}
      disabled
      className="block w-full h-11 px-4 rounded-md border border-gray-200 bg-gray-100 text-gray-600 cursor-not-allowed"
    />
  </div>

  {/* Bio */}
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">
      Bio <span className="text-gray-400">(max 160 characters)</span>
    </label>
    <textarea
      name="bio"
      value={formData.bio}
      onChange={handleChange}
      maxLength={160}
      rows={3}
      className="block w-full px-4 py-3 rounded-md border border-gray-300 focus:ring-indigo-500 focus:border-indigo-500 resize-none bg-white"
    />
  </div>

  {/* Roles */}
  {/* <div>
    <label className="block text-sm font-medium text-gray-700 mb-2">Roles *</label>
    <div className="flex flex-wrap gap-3">
      {Object.keys(formData.roles).map((role) => (
        <button
          key={role}
          type="button"
          onClick={() => handleRoleChange(role)}
          className={`px-4 py-2 rounded-full text-xs font-semibold tracking-wide ${
            formData.roles[role]
              ? 'bg-indigo-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          } transition`}
        >
          {role.charAt(0).toUpperCase() + role.slice(1)}
        </button>
      ))}
    </div>
  </div> */}

  {/* Interest Domains */}
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-2">
      Interest Domains <span className="text-gray-400">(Optional)</span>
    </label>
    <p className="text-xs text-gray-500 mb-2">
      Select domains you're interested or experienced in
    </p>
    <div className="flex flex-wrap gap-2 mb-3 min-h-[36px]">
      {formData.creatorDomains?.length > 0 ? (
        formData.creatorDomains.map(domain => (
          <div key={domain} className="inline-flex items-center bg-indigo-100 text-indigo-800 rounded-full px-3 py-1 text-sm">
            <span className="mr-1">{domain}</span>
            <button
              type="button"
              onClick={() => setFormData(prev => ({
                ...prev,
                creatorDomains: prev.creatorDomains.filter(d => d !== domain)
              }))}
              className="text-indigo-600 hover:text-indigo-800 font-bold"
            >
              ×
            </button>
          </div>
        ))
      ) : (
        <div className="text-gray-400 text-sm">No domains selected</div>
      )}
    </div>
    <select
      value=""
      onChange={(e) => {
        if (e.target.value) {
          const domain = e.target.value;
          if (!formData.creatorDomains.includes(domain)) {
            setFormData(prev => ({
              ...prev,
              creatorDomains: [...(prev.creatorDomains || []), domain]
            }));
          }
          e.target.value = "";
        }
      }}
      className="block w-full h-11 px-4 rounded-md border border-gray-300 focus:border-indigo-500 focus:ring-indigo-500 bg-white"
    >
      <option value="">-- Select domain --</option>
      {DOMAINS.filter(domain =>
        !formData.creatorDomains.includes(domain)
      ).map(domain => (
        <option key={domain} value={domain}>{domain}</option>
      ))}
    </select>
  </div>

  {/* Skills */}
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-2">
      Skills <span className="text-gray-400">(Optional)</span>
    </label>
    <p className="text-xs text-gray-500 mb-2">
      Select skills you bring to projects
    </p>
    <div className="flex flex-wrap gap-2 mb-3 min-h-[36px]">
      {formData.contributorSkills?.length > 0 ? (
        formData.contributorSkills.map(skill => (
          <div key={skill} className="inline-flex items-center bg-blue-100 text-blue-800 rounded-full px-3 py-1 text-sm">
            <span className="mr-1">{skill}</span>
            <button
              type="button"
              onClick={() => setFormData(prev => ({
                ...prev,
                contributorSkills: prev.contributorSkills.filter(s => s !== skill)
              }))}
              className="text-blue-600 hover:text-blue-800 font-bold"
            >
              ×
            </button>
          </div>
        ))
      ) : (
        <div className="text-gray-400 text-sm">No skills selected</div>
      )}
    </div>
    <select
      value=""
      onChange={(e) => {
        if (e.target.value) {
          const skill = e.target.value;
          if (!formData.contributorSkills.includes(skill)) {
            setFormData(prev => ({
              ...prev,
              contributorSkills: [...(prev.contributorSkills || []), skill]
            }));
          }
          e.target.value = "";
        }
      }}
      className="block w-full h-11 px-4 rounded-md border border-gray-300 focus:border-indigo-500 focus:ring-indigo-500 bg-white"
    >
      <option value="">-- Select skill --</option>
      {SKILLS.filter(skill =>
        !formData.contributorSkills.includes(skill)
      ).map(skill => (
        <option key={skill} value={skill}>{skill}</option>
      ))}
    </select>
  </div>

  {/* Social Links */}
  <div className="space-y-4">
    <label className="block text-sm font-medium text-gray-700">Social Links <span className="text-gray-400">(Optional)</span></label>
    {Object.keys(formData.socials).map(platform => (
      <div key={platform} className="flex items-center space-x-3">
        <span className="w-24 text-sm text-gray-500 capitalize">{platform}:</span>
        <input
          type="text"
          value={formData.socials[platform]}
          onChange={(e) => handleSocialChange(platform, e.target.value)}
          placeholder={`Enter ${platform} link/username`}
          className="block w-full h-10 px-4 rounded-md border border-gray-300 focus:border-indigo-500 focus:ring-indigo-500 bg-white"
        />
      </div>
    ))}
  </div>

  {/* Portfolio Links */}
  <div className="space-y-4">
    <label className="block text-sm font-medium text-gray-700">
      Portfolio Links <span className="text-gray-400">(Optional, max 3)</span>
    </label>
    {formData.portfolioLinks.map((link, idx) => (
      <input
        key={idx}
        type="url"
        value={link}
        onChange={(e) => handlePortfolioLinkChange(idx, e.target.value)}
        placeholder="https://"
        className="block w-full h-10 px-4 rounded-md border border-gray-300 focus:border-indigo-500 focus:ring-indigo-500 bg-white"
      />
    ))}
    {formData.portfolioLinks.length < 3 && (
      <button
        type="button"
        onClick={addPortfolioLink}
        className="text-indigo-600 hover:underline text-sm font-medium"
      >
        + Add Portfolio Link
      </button>
    )}
  </div>

  {/* Buttons */}
  <div className="flex justify-end space-x-4">
    <button
      type="button"
      onClick={onClose}
      className="px-5 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-100 transition"
    >
      Cancel
    </button>
    <button
      type="submit"
      disabled={loading}
      className="px-5 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 transition"
    >
      {loading ? 'Saving...' : isUpdate ? 'Update Profile' : 'Create Profile'}
    </button>
  </div>
</form>

  );
}

export default InnovatorForm; 